import { mapGetters, mapActions } from 'vuex'
import { equals } from 'ramda'

import { SOURCE } from 'enums/source'
import {
  ARTICLE_PAGE_UPDATED_EVENT,
  ARTICLES_FEED_UPDATED_EVENT
} from 'enums/article-auto-refresh'
import {
  SignalR,
  REQUIRED_ACTION,
  REQUIRED_ACTION_BY_DYNAMIC_STATE
} from 'enums/signalr'
import {
  LIVE_BYTES_ADDED_EVENT,
  LIVE_BYTES_DELETED_EVENT,
  LIVE_BYTES_EDITED_EVENT,
  LIVE_BYTES_RECONNECT_EVENT
} from '@/components/_layout/ASidebar/LiveBytes/enums'

export default {
  data() {
    return {
      hubConnection: null,
      hubConnectionImportPromise: null,
      signalR_signalRSettings: [
        {
          hostPath: this.$env.IL_API_URL,
          hubKey: 'FmBellIconNotifications',
          hubPath: '/hubs/bell-icon-notifications',
          listeners: [
            {
              name: 'CountInfo',
              handler: this.handleNewFmMessage
            }
          ],
          startupInvokes: [
            {
              name: 'Init',
              payloadFn: () => this.$helper.getTokenValue(this.accessToken)
            }
          ],
          shouldHubBeConnected: () => this.isLoggedIn,
          onDisconnect: this.clearNewFmMessages,
          onError: this.handleError
        },
        {
          hostPath: this.$env.IL_API_URL,
          hubKey: 'IlBellIconNotifications',
          hubPath: '/hubs/bell-icon-notifications',
          listeners: [
            {
              name: 'CountInfo',
              handler: this.handleNewIlMessage
            }
          ],
          startupInvokes: [
            {
              name: 'Init',
              payloadFn: () => this.$helper.getTokenValue(this.accessToken)
            }
          ],
          shouldHubBeConnected: () => this.isLoggedIn,
          onDisconnect: this.clearNewIlMessages,
          onError: this.handleError
        },
        {
          hostPath: this.$env.IL_API_URL,
          hubKey: 'IlAutoRefresh',
          hubPath: '/hubs/auto-refresh-content',
          listeners: [
            {
              name: 'ArticlesFeedUpdated',
              handler: this.handleArticlesFeedUpdated
            },
            {
              name: 'ArticlePageUpdated',
              handler: this.handleArticlePageUpdated
            }
          ],
          startupInvokes: [],
          shouldHubBeConnected: () => true,
          onError: this.handleError
        },
        {
          hostPath: this.$env.IL_API_URL,
          hubKey: 'IlLiveBytes',
          hubPath: '/hubs/live-bytes',
          listeners: [
            {
              name: 'LiveBytesAdded',
              handler: this.handleLiveBytesAdded
            },
            {
              name: 'LiveBytesDeleted',
              handler: this.handleLiveBytesDeleted
            },
            {
              name: 'LiveBytesEdited',
              handler: this.handleLiveBytesEdited
            },
            /**
             * TODO refactor: right now, after the tab becomes active after long
             * inactivity time (PAUSE_CONNECTION_TIMEOUT), we trigger
             * emitAllHandlers in SignalR class.
             * This approach is not ideal, and it only works relatively good
             * for refreshing articles. However, it's meaningless to trigger
             * LiveBytesAdded or LiveBytesDeleted or LiveBytesEdited after
             * such reconnect as there is no meaningful payload for these events
             * when it happens. We need a separate global event for such case
             * that should not be mixed with actual Websocket listeners.
             *
             * The suggestion is to create and just trigger a global event and
             * let subscribers decide what to update based on it.
             * For now we will use the simplest approach to speed up the
             * delivery of the hotfix.
             */
            {
              name: 'Reconnect',
              handler: this.handleLiveBytesReconnect
            }
          ],
          startupInvokes: [],
          shouldHubBeConnected: () => true,
          onError: this.handleError
        }
      ],
      signalR_messageCount: {
        [SOURCE.FM]: 0,
        [SOURCE.FL]: 0
      },
      signalR_hubConnections: {}
    }
  },
  computed: {
    ...mapGetters({
      accessToken: 'auth/accessToken',
      isLoggedIn: 'auth/isLoggedIn',
      isOnline: 'isOnline',
      isBrowserTabActiveAndFocused: 'isBrowserTabActiveAndFocused'
    }),
    $_signalr_dynamicStateToWatch() {
      if (!process.client) return

      return this.signalR_signalRSettings.reduce((acc, hubSettings) => {
        const isSignalrInitialized = !!window.signalR
        const { hostPath, hubKey } = hubSettings

        const isHubConnected =
          !!this.signalR_hubConnections[hubKey] &&
          this.signalR_hubConnections[hubKey].isConnected
        const isConnectionRequired = hubSettings.shouldHubBeConnected()

        const hubData = {
          hostPath,
          hubKey,
          hubSettings,
          isSignalrInitialized,
          isHubConnected,
          isConnectionRequired,
          isOnline: this.isOnline,
          isBrowserTabActiveAndFocused: this.isBrowserTabActiveAndFocused
        }

        return [...acc, hubData]
      }, [])
    },
    totalNewMessageCount() {
      return (
        this.signalR_messageCount[SOURCE.FM] +
        this.signalR_messageCount[SOURCE.FL]
      )
    }
  },
  watch: {
    $_signalr_dynamicStateToWatch: {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        /**
         * We check if newVal and oldVal are different because we found the cases
         * when they are equal. Such cases might cause double connect issue and
         * errors during the second connection.
         */
        if (!process.client || equals(newVal, oldVal)) return

        newVal.forEach(newHubSettings => {
          const { hubSettings, hubKey } = newHubSettings

          const requiredAction = this.getRequiredActionBySettings(
            newHubSettings
          )
          const isConnectRequired = [
            REQUIRED_ACTION.CONNECT,
            REQUIRED_ACTION.RECONNECT
          ].includes(requiredAction)

          const isHubAlreadyExist = !!this.signalR_hubConnections[hubKey]

          /** Create new SignalR instance **/
          if (!isHubAlreadyExist && isConnectRequired) {
            this.signalR_hubConnections[hubKey] = new SignalR({
              settings: hubSettings
            })
            this.startInitialNotificationCountRequest({
              args: { source: hubSettings.source }
            })
            return
          }

          /** Use existing Signalr instance **/
          switch (requiredAction) {
            case REQUIRED_ACTION.CONNECT:
              this.signalR_hubConnections[hubKey].connect()
              this.startInitialNotificationCountRequest({
                args: { source: hubSettings.source }
              })
              break
            case REQUIRED_ACTION.DISCONNECT:
              this.signalR_hubConnections[hubKey].disconnect()
              break
            case REQUIRED_ACTION.PAUSE_ON_TIMEOUT:
              this.signalR_hubConnections[hubKey].startPauseTimeout()
              break
            case REQUIRED_ACTION.CANCEL_PAUSE_TIMEOUT:
              this.signalR_hubConnections[hubKey].cancelPauseTimeout()
              break
            case REQUIRED_ACTION.RECONNECT:
              this.signalR_hubConnections[hubKey].reconnect()
          }
        })
      }
    }
  },
  methods: {
    ...mapActions({
      requestSetNewNotificationCount:
        'notifications/requestSetNewNotificationCount',
      startInitialNotificationCountRequest:
        'notifications/startInitialNotificationCountRequest',
      completeInitialNotificationCountRequest:
        'notifications/completeInitialNotificationCountRequest'
    }),
    getRequiredActionBySettings(settings) {
      const {
        isSignalrInitialized,
        isConnectionRequired,
        isHubConnected,
        isOnline,
        isBrowserTabActiveAndFocused
      } = settings

      const boolResult = this.$helper.getBinaryStringFromBooleanArray([
        isSignalrInitialized,
        isOnline && isBrowserTabActiveAndFocused,
        isHubConnected,
        isConnectionRequired
      ])

      return (
        REQUIRED_ACTION_BY_DYNAMIC_STATE[boolResult] || REQUIRED_ACTION.NOTHING
      )
    },
    async handleNewFmMessage(message) {
      this.signalR_messageCount[SOURCE.FM] = this.getNewMessageCount(message)
      this.requestSetNewNotificationCount(this.totalNewMessageCount)
      await this.completeInitialNotificationCountRequest({
        args: { source: SOURCE.FM }
      })
    },
    async handleNewIlMessage(message) {
      this.signalR_messageCount[SOURCE.FL] = this.getNewMessageCount(message)
      this.requestSetNewNotificationCount(this.totalNewMessageCount)
      await this.completeInitialNotificationCountRequest({
        args: { source: SOURCE.FL }
      })
    },
    getNewMessageCount(message) {
      return message
        ? Object.values(message).reduce((acc, count) => acc + count, 0)
        : 0
    },
    clearNewFmMessages() {
      this.signalR_messageCount[SOURCE.FM] = 0
      this.requestSetNewNotificationCount(this.totalNewMessageCount)
    },
    clearNewIlMessages() {
      this.signalR_messageCount[SOURCE.FL] = 0
      this.requestSetNewNotificationCount(this.totalNewMessageCount)
    },
    handleArticlesFeedUpdated(payload) {
      this.$bus.$emit(ARTICLES_FEED_UPDATED_EVENT, payload)
    },
    handleArticlePageUpdated(payload) {
      this.$bus.$emit(ARTICLE_PAGE_UPDATED_EVENT, payload)
    },
    handleLiveBytesAdded(payload) {
      if (!payload) return

      const { LiveBytes } = payload
      this.$bus.$emit(LIVE_BYTES_ADDED_EVENT, LiveBytes)
    },
    handleLiveBytesDeleted(payload) {
      if (!payload) return

      const { LiveByteIds } = payload
      this.$bus.$emit(LIVE_BYTES_DELETED_EVENT, LiveByteIds)
    },
    handleLiveBytesEdited(payload) {
      if (!payload) return

      const { LiveBytes } = payload
      this.$bus.$emit(LIVE_BYTES_EDITED_EVENT, LiveBytes)
    },
    handleLiveBytesReconnect() {
      this.$bus.$emit(LIVE_BYTES_RECONNECT_EVENT)
    },
    handleError({ err, errorMessage }) {
      const error = err || new Error(errorMessage)
      this.$errorHandler(error, this, {
        showMessage: false,
        message: errorMessage
      })
    }
  },
  beforeDestroy() {
    Object.values(this.signalR_hubConnections).forEach(host => {
      host.disconnect()
    })
  }
}
